import { registerNamespaceResetter } from "../connection/registry";
import { type Listener } from "../connection/types";

/**
 * Every namespace store tracks when the most recent event was received and
 * the latest listener error (or null when healthy). The factory enforces this
 * baseline shape so error and event-timestamp handling can live in one place.
 */
export type BaseListenerState = {
  lastEventAt: null | number;
  lastListenerError: null | string;
};

export type NamespaceStoreConfig<TState extends BaseListenerState, TEvent> = {
  /**
   * Fold a parsed event into state. Receives the arrival timestamp so that
   * `lastEventAt` and any derived per-device status maps stay in sync. Must
   * return a new state value (stores rely on reference equality for
   * `useSyncExternalStore`).
   */
  applyEvent: (state: TState, event: TEvent, at: number) => TState;
  /** Factory for a fresh state value; invoked on init and on reset. */
  createInitialState: () => TState;
  /** Short human label used in `console.warn` output. */
  label: string;
  /**
   * Parse the opaque event payload delivered by qz-tray into a typed event, or
   * return `null` to signal "we don't know what this is". A `null` return
   * yields a warning and sets `lastListenerError`, but does not throw.
   */
  parseEvent: (raw: unknown) => TEvent | null;
  /**
   * Register the namespace-level callback with qz-tray (e.g.
   * `qz.serial.setSerialCallbacks`). Called exactly once on first init; the
   * callback forwards to the factory's internal parse/record pipeline.
   */
  registerCallback: (handler: (raw: unknown) => void) => void;
};

export type NamespaceStore<TState extends BaseListenerState, TEvent> = {
  /**
   * Idempotently wires the qz-tray callback and registers the resetter. Safe
   * to call from hooks, mutations, or on first subscribe — the factory guards
   * against double registration.
   */
  ensureInitialized: () => void;
  getSnapshot: () => TState;
  recordEvent: (event: TEvent) => void;
  reset: () => void;
  setListenerError: (message: null | string) => void;
  setState: (updater: (state: TState) => TState) => void;
  subscribe: (listener: Listener) => () => void;
};

export const createNamespaceStore = <TState extends BaseListenerState, TEvent>(
  config: NamespaceStoreConfig<TState, TEvent>,
): NamespaceStore<TState, TEvent> => {
  const listeners = new Set<Listener>();
  let state = config.createInitialState();
  let initialized = false;

  const emit = () => {
    for (const listener of listeners) listener();
  };

  const setState = (updater: (state: TState) => TState) => {
    state = updater(state);
    emit();
  };

  const reset = () => {
    state = config.createInitialState();
    emit();
  };

  const setListenerError = (message: null | string) => {
    setState((s) => ({ ...s, lastListenerError: message }));
  };

  const recordEvent = (event: TEvent) => {
    const at = Date.now();
    setState((s) => config.applyEvent(s, event, at));
  };

  const ensureInitialized = () => {
    if (initialized) return;

    initialized = true;

    config.registerCallback((raw) => {
      const event = config.parseEvent(raw);

      if (!event) {
        // eslint-disable-next-line no-console
        console.warn(`Unexpected ${config.label} event`, raw);
        setListenerError(`Unexpected ${config.label} event`);
        return;
      }

      setListenerError(null);
      recordEvent(event);
    });

    registerNamespaceResetter(reset);
  };

  const subscribe = (listener: Listener) => {
    ensureInitialized();
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  };

  const getSnapshot = () => state;

  return {
    ensureInitialized,
    getSnapshot,
    recordEvent,
    reset,
    setListenerError,
    setState,
    subscribe,
  };
};
